import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useRole } from '@/hooks/useRole';
import { useAuth } from '@/app/providers/AuthProvider';
import { useStaff } from '@/features/staff/useStaff';
import { useCreateTask, useUpdateTask, type CreateTaskInput } from './useTasks';
import { TASK_PRIORITY_LABELS, type Task, type TaskPriority, type TaskWithClient } from './types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  edit?: Task | TaskWithClient;
}

export function TaskModal({ open, onOpenChange, edit }: Props) {
  const { clientId } = useCurrentClient();
  const { isManager } = useRole();
  const { user } = useAuth();
  const { data: staff = [] } = useStaff();
  const create = useCreateTask();
  const update = useUpdateTask();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [dueAt, setDueAt] = useState<string>('');
  const [linkToClient, setLinkToClient] = useState(true);
  const [assigneeUserId, setAssigneeUserId] = useState<string>('');

  // Colaboradores com login ativo — só esses podem ser responsáveis (FK auth.users).
  const assignableStaff = staff.filter((s) => s.active && s.user_id);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (open && edit) {
      setTitle(edit.title);
      setDescription(edit.description ?? '');
      setPriority(edit.priority);
      setDueAt(edit.due_at ? format(new Date(edit.due_at), "yyyy-MM-dd'T'HH:mm") : '');
      setLinkToClient(!!edit.client_id);
      setAssigneeUserId(edit.assigned_to_user_id ?? '');
    } else if (open) {
      setTitle('');
      setDescription('');
      setPriority('normal');
      setDueAt('');
      setLinkToClient(!!clientId);
      setAssigneeUserId(user?.id ?? '');
    }
    // Não inclui `user?.id` na dep: a inicialização do assignee só precisa
    // acontecer ao abrir o modal. Se o supabase-js dispara TOKEN_REFRESHED
    // (mesma user.id, novo objeto user), não queremos resetar o form que a
    // Anny estava preenchendo.
  }, [open, edit, clientId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;

    const due_at = dueAt ? new Date(dueAt).toISOString() : null;

    if (edit) {
      await update.mutateAsync({
        id: edit.id,
        updates: {
          title: trimmed,
          description: description.trim() || null,
          priority,
          due_at,
          ...(isManager && assigneeUserId ? { assigned_to_user_id: assigneeUserId } : {}),
        },
      });
    } else {
      const input: CreateTaskInput = {
        title: trimmed,
        description: description.trim() || null,
        priority,
        due_at,
        client_id: linkToClient && clientId ? clientId : null,
        ...(isManager && assigneeUserId ? { assigned_to_user_id: assigneeUserId } : {}),
      };
      await create.mutateAsync(input);
    }
    onOpenChange(false);
  };

  const submitting = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-brand">{edit ? 'Editar tarefa' : 'Nova tarefa'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="task-title">Título *</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Conciliar OFX da Clínica X"
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="task-desc">Descrição</Label>
            <Textarea
              id="task-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detalhes (opcional)"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Prioridade</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TaskPriority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TASK_PRIORITY_LABELS) as TaskPriority[]).map((p) => (
                    <SelectItem key={p} value={p}>
                      {TASK_PRIORITY_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-due">Prazo</Label>
              <Input
                id="task-due"
                type="datetime-local"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
              />
            </div>
          </div>
          {isManager && (
            <div className="space-y-1.5">
              <Label>Responsável</Label>
              <Select value={assigneeUserId} onValueChange={setAssigneeUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um colaborador" />
                </SelectTrigger>
                <SelectContent>
                  {assignableStaff.map((s) => (
                    <SelectItem key={s.id} value={s.user_id as string}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!edit && clientId && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={linkToClient}
                onChange={(e) => setLinkToClient(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Associar ao cliente atual
            </label>
          )}
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {edit ? 'Salvar' : 'Criar tarefa'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
